<template>
  <aside class="conversation-list">
    <header class="list-header">
      <div>
        <h2>{{ language.t('contacts.title') }}</h2>
        <p>{{ language.t('contacts.count', { count: contacts.contacts.length }) }}</p>
      </div>
      <div class="header-actions">
        <button class="icon-button" :title="language.t('list.addContact')" :aria-label="language.t('list.addContact')" @click="openAddContact">＋</button>
        <button class="icon-button" :title="language.t('common.refresh')" @click="contacts.refreshContacts({ force: true })">↻</button>
      </div>
    </header>

    <div class="search-box">
      <input v-model.trim="keyword" :placeholder="language.t('contacts.search')">
      <button type="button" class="friend-notice-entry" @click="openFriendRequests">
        <span class="friend-notice-icon" aria-hidden="true">✉</span>
        <span class="friend-notice-copy">
          <strong>好友通知</strong>
          <small v-if="contacts.pendingApplicationCount">{{ formatBadgeCount(contacts.pendingApplicationCount) }} 条好友申请待处理</small>
          <small v-else>暂无新的好友申请</small>
        </span>
        <span v-if="contacts.pendingApplicationCount" class="friend-notice-count">
          {{ formatBadgeCount(contacts.pendingApplicationCount) }}
        </span>
        <span class="friend-notice-arrow" aria-hidden="true">›</span>
      </button>
    </div>

    <div class="sidebar-scroll">
      <button
        v-for="item in filteredContacts"
        :key="item.id"
        class="conversation-item"
        :class="{ active: item.id === contacts.activeId }"
        @click="selectContact(item.id)"
      >
        <img v-if="item.portrait" class="conversation-avatar image-avatar" :src="item.portrait" alt="">
        <span v-else class="conversation-avatar">{{ item.avatarText }}</span>
        <span class="conversation-copy">
          <strong>{{ item.title }}</strong>
          <small>{{ item.subtitle }}</small>
        </span>
      </button>

      <div v-if="!filteredContacts.length" class="empty-list">
        <strong>{{ contacts.loading ? language.t('common.loading') : language.t('contacts.empty') }}</strong>
        <span>{{ contacts.loading ? language.t('common.pleaseWait') : language.t('contacts.emptyHint') }}</span>
      </div>
    </div>

    <div v-if="addingContact" class="modal-backdrop" @click.self="closeAddContact">
      <form class="modal-panel" @submit.prevent="submitAddContact">
        <header>
          <strong>{{ language.t('list.addContact') }}</strong>
          <button type="button" class="icon-button" :title="language.t('common.close')" @click="closeAddContact">×</button>
        </header>
        <label>
          <span>{{ language.t('list.userKeyword') }}</span>
          <input v-model.trim="addKeyword" autocomplete="off" :placeholder="language.t('list.userKeywordPlaceholder')">
        </label>
        <p v-if="contacts.actionError" class="form-error">{{ language.error(contacts.actionError) }}</p>
        <p v-if="contacts.actionInfo" class="form-info">{{ contacts.actionInfo }}</p>
        <div class="modal-actions">
          <button type="button" class="ghost-inline-action" @click="closeAddContact">{{ language.t('common.cancel') }}</button>
          <button class="primary-inline-action" :disabled="contacts.loading || !addKeyword || Boolean(addResult)">
            {{ contacts.loading ? language.t('contacts.adding') : addResult ? language.t(addResult.pending ? 'contacts.requestSent' : 'contacts.added') : language.t('common.add') }}
          </button>
        </div>
      </form>
    </div>

    <div v-if="requestsOpen" class="modal-backdrop" @click.self="closeFriendRequests">
      <section class="modal-panel friend-request-panel">
        <header>
          <strong>{{ language.t('contacts.friendRequests') }}</strong>
          <button type="button" class="icon-button" :title="language.t('common.close')" @click="closeFriendRequests">×</button>
        </header>

        <div v-if="contacts.applicationsLoading" class="friend-request-state">
          <strong>{{ language.t('common.loading') }}</strong>
          <span>{{ language.t('common.pleaseWait') }}</span>
        </div>
        <div v-else-if="!contacts.pendingFriendApplications.length" class="friend-request-state">
          <strong>{{ language.t('contacts.noFriendRequests') }}</strong>
          <span>{{ language.t('contacts.noFriendRequestsHint') }}</span>
        </div>
        <div v-else class="friend-request-list">
          <article
            v-for="application in contacts.pendingFriendApplications"
            :key="application.id"
            class="friend-request-row"
          >
            <img v-if="application.portrait" class="conversation-avatar image-avatar" :src="application.portrait" alt="">
            <span v-else class="conversation-avatar">{{ application.avatarText }}</span>
            <span class="friend-request-copy">
              <strong>{{ application.title }}</strong>
              <small>{{ language.t('contacts.applicationFrom', { userId: application.fromUserID }) }}</small>
              <span v-if="application.reqMsg">{{ application.reqMsg }}</span>
            </span>
            <span class="friend-request-actions">
              <button
                type="button"
                class="ghost-inline-action"
                :disabled="isApplicationBusy(application)"
                @click="refuseApplication(application)"
              >
                {{ language.t('contacts.refuse') }}
              </button>
              <button
                type="button"
                class="primary-inline-action"
                :disabled="isApplicationBusy(application)"
                @click="acceptApplication(application)"
              >
                {{ isApplicationBusy(application) ? language.t('common.processing') : language.t('contacts.accept') }}
              </button>
            </span>
          </article>
        </div>

        <p v-if="contacts.actionError" class="form-error">{{ language.error(contacts.actionError) }}</p>
        <p v-else-if="contacts.actionInfo" class="form-info">{{ contacts.actionInfo }}</p>
      </section>
    </div>
  </aside>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useContactStore } from '@/stores/contacts';
import { useLanguageStore } from '@/stores/language';

const contacts = useContactStore();
const language = useLanguageStore();
const emit = defineEmits(['detail-open']);
const keyword = ref('');
const addingContact = ref(false);
const requestsOpen = ref(false);
const addKeyword = ref('');
const addResult = ref(null);
watch(addKeyword, () => { addResult.value=null; });

const filteredContacts = computed(() => {
  const value = keyword.value.toLowerCase();
  if (!value) {
    return contacts.contacts;
  }
  return contacts.contacts.filter(item => {
    return item.title.toLowerCase().includes(value)
      || item.subtitle.toLowerCase().includes(value)
      || item.uid.toLowerCase().includes(value);
  });
});

onMounted(() => {
  contacts.refreshFriendApplications().catch(() => {});
});

function selectContact(contactId) {
  contacts.setActiveContact(contactId);
  emit('detail-open');
}

function openAddContact() {
  contacts.actionError = '';
  contacts.actionInfo = '';
  addKeyword.value = '';
  addingContact.value = true;
}

function closeAddContact() {
  addingContact.value = false;
}

function openFriendRequests() {
  contacts.actionError = '';
  contacts.actionInfo = '';
  requestsOpen.value = true;
  contacts.refreshFriendApplications({ force: true }).catch(() => {});
}

function closeFriendRequests() {
  requestsOpen.value = false;
}

async function submitAddContact() {
  if(addResult.value||contacts.loading)return;
  const input=addKeyword.value;
  const result=await contacts.addContact(input);
  if(result&&input===addKeyword.value)addResult.value=result;
}

function isApplicationBusy(application) {
  return contacts.applicationBusyIds.includes(String(application?.id || application?.fromUserID || ''));
}

function acceptApplication(application) {
  contacts.acceptFriendApplication(application);
}

function refuseApplication(application) {
  contacts.refuseFriendApplication(application);
}

function formatBadgeCount(count) {
  return Number(count || 0) > 99 ? '99+' : String(count || 0);
}
</script>
